import { useState } from 'react';
import api from '../api';
import {
  Cake,
  Play,
  TestTube,
  Gift,
  Search,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Crown,
  TrendingUp,
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function JobsTesting() {
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [jobResults, setJobResults] = useState(null);

  // Test birthday for a specific user
  const handleTestBirthday = async () => {
    if (!userId.trim()) {
      toast.error('Please enter a user ID');
      return;
    }

    setLoading(true);
    setTestResults(null);
    try {
      const response = await api.post('/admin/jobs/test-birthday', { userId: userId.trim() });
      setTestResults(response.data);
      toast.success('Birthday test complete');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to test birthday');
    } finally {
      setLoading(false);
    }
  };

  // Trigger actual birthday rewards processing
  const handleTriggerBirthdayRewards = async () => {
    setLoading(true);
    setJobResults(null);
    try {
      const response = await api.post('/admin/jobs/birthday-rewards', 
        userId.trim() ? { userId: userId.trim() } : {}
      );
      setJobResults({
        type: 'birthday-rewards',
        ...response.data,
      });
      toast.success(`Birthday rewards processed: ${response.data.assignedCount} assigned`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to process birthday rewards');
    } finally {
      setLoading(false);
    }
  };

  // Trigger birthday notifications
  const handleTriggerBirthdayNotifications = async () => {
    setLoading(true);
    setJobResults(null);
    try {
      const response = await api.post('/admin/jobs/birthday-notifications');
      setJobResults({
        type: 'birthday-notifications',
        ...response.data,
      });
      toast.success(`Birthday notifications sent: ${response.data.sentCount} sent`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send notifications');
    } finally {
      setLoading(false);
    }
  };

  // Manually assign a birthday reward
  const handleAssignReward = async (rewardId, rewardTitle) => {
    if (!userId.trim()) {
      toast.error('Please enter a user ID first');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/admin/jobs/assign-birthday-reward', {
        userId: userId.trim(),
        rewardId,
      });
      
      if (response.data.assigned) {
        toast.success(`Assigned "${rewardTitle}" to user`);
        // Refresh test results
        handleTestBirthday();
      } else {
        toast.error('Reward was already assigned or user not eligible');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign reward');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <TestTube className="text-amber-500" />
            Jobs & Trigger Testing
          </h1>
          <p className="text-gray-400 mt-1">
            Test scheduled jobs and reward triggers before they run automatically
          </p>
        </div>
      </div>

      {/* User ID Input */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <User size={20} />
          Target User
        </h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="Enter user UUID (e.g., 1aa5c85e-37e4-4218-be27-943ab693080a)"
            className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500"
          />
          <button
            onClick={handleTestBirthday}
            disabled={loading || !userId.trim()}
            className="px-6 py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
            Test Birthday
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          This simulates what rewards the user would receive if today was their birthday
        </p>
      </div>

      {/* Test Results */}
      {testResults && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Cake size={20} className="text-pink-400" />
            Birthday Test Results
          </h2>
          
          {/* User Info */}
          <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Email:</span>
                <p className="text-white font-medium">{testResults.user.email}</p>
              </div>
              <div>
                <span className="text-gray-400">Name:</span>
                <p className="text-white font-medium">{testResults.user.firstName || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-400 flex items-center gap-1">
                  <Crown size={14} /> Tier:
                </span>
                <p className="text-amber-400 font-medium capitalize">{testResults.user.tier}</p>
              </div>
              <div>
                <span className="text-gray-400 flex items-center gap-1">
                  <TrendingUp size={14} /> Level:
                </span>
                <p className="text-white font-medium">{testResults.user.level}</p>
              </div>
            </div>
            {testResults.user.actualBirthday && (
              <p className="text-xs text-gray-500 mt-2">
                Actual birthday: {new Date(testResults.user.actualBirthday).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-400">{testResults.summary.wouldAssign}</p>
              <p className="text-xs text-green-300">Would Assign</p>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-yellow-400">{testResults.summary.alreadyAssigned}</p>
              <p className="text-xs text-yellow-300">Already Assigned</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{testResults.summary.conditionsNotMet}</p>
              <p className="text-xs text-red-300">Conditions Not Met</p>
            </div>
          </div>

          {/* Reward Details */}
          <div className="space-y-3">
            {testResults.birthdayRewards.map((reward) => (
              <div 
                key={reward.rewardId}
                className={`border rounded-lg p-4 ${
                  reward.wouldAssign 
                    ? 'border-green-500/30 bg-green-500/5' 
                    : reward.alreadyAssigned 
                      ? 'border-yellow-500/30 bg-yellow-500/5'
                      : 'border-red-500/30 bg-red-500/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {reward.wouldAssign ? (
                      <CheckCircle className="text-green-400" size={20} />
                    ) : reward.alreadyAssigned ? (
                      <AlertCircle className="text-yellow-400" size={20} />
                    ) : (
                      <XCircle className="text-red-400" size={20} />
                    )}
                    <div>
                      <p className="text-white font-medium">{reward.title}</p>
                      <p className="text-xs text-gray-400">
                        {reward.isMultiTrigger ? `Multi-trigger (${reward.triggerLogic})` : 'Single trigger'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {reward.wouldAssign && (
                      <button
                        onClick={() => handleAssignReward(reward.rewardId, reward.title)}
                        disabled={loading}
                        className="px-3 py-1 bg-green-500 text-black text-sm font-medium rounded hover:bg-green-400 disabled:opacity-50"
                      >
                        Assign Now
                      </button>
                    )}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      reward.wouldAssign 
                        ? 'bg-green-500/20 text-green-400' 
                        : reward.alreadyAssigned 
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                    }`}>
                      {reward.wouldAssign ? 'Will Assign' : reward.alreadyAssigned ? 'Already Assigned' : 'Not Eligible'}
                    </span>
                  </div>
                </div>
                
                {/* Condition Details */}
                {reward.conditions && reward.conditions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <p className="text-xs text-gray-500 mb-2">Conditions:</p>
                    <div className="flex flex-wrap gap-2">
                      {reward.conditions.map((cond, idx) => (
                        <span 
                          key={idx}
                          className={`px-2 py-1 rounded text-xs ${
                            cond.passed 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {cond.type === 'birthday' && '🎂 Birthday'}
                          {cond.type === 'tier_current' && `👑 Tier: ${cond.required} (user: ${cond.actual})`}
                          {cond.type === 'level_current' && `📈 Level: ${cond.required}+ (user: ${cond.actual})`}
                          {cond.passed ? ' ✓' : ' ✗'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Job Triggers */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Play size={20} className="text-green-400" />
          Run Jobs Manually
        </h2>
        <p className="text-gray-400 text-sm mb-4">
          These jobs normally run automatically via cron schedule. Use these buttons to trigger them manually.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-pink-500/20 rounded-lg">
                <Gift className="text-pink-400" size={24} />
              </div>
              <div>
                <p className="text-white font-medium">Birthday Rewards</p>
                <p className="text-xs text-gray-500">Runs daily at 7 AM Pacific</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-3">
              Assigns birthday-triggered rewards to users whose birthday is today.
              {userId.trim() && <span className="text-amber-400"> Will only process for the specified user.</span>}
            </p>
            <button
              onClick={handleTriggerBirthdayRewards}
              disabled={loading}
              className="w-full py-2 bg-pink-500 text-white font-medium rounded-lg hover:bg-pink-400 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Play size={18} />}
              Run Birthday Rewards Job
            </button>
          </div>

          <div className="border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Cake className="text-blue-400" size={24} />
              </div>
              <div>
                <p className="text-white font-medium">Birthday Notifications</p>
                <p className="text-xs text-gray-500">Runs daily at 9 AM Pacific</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-3">
              Sends push notifications to users whose birthday is today.
            </p>
            <button
              onClick={handleTriggerBirthdayNotifications}
              disabled={loading}
              className="w-full py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-400 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Play size={18} />}
              Run Birthday Notifications Job
            </button>
          </div>
        </div>

        {/* Job Results */}
        {jobResults && (
          <div className="mt-4 p-4 bg-gray-700/50 rounded-lg">
            <p className="text-green-400 font-medium">
              ✓ {jobResults.message}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {jobResults.type === 'birthday-rewards' 
                ? `${jobResults.assignedCount} reward(s) assigned`
                : `${jobResults.sentCount} notification(s) sent`
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
